import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import React, { useContext, useEffect, useState } from 'react';
import Pagination from '@atlaskit/pagination';
import { CCGroupPicker } from '../../components/ui/CCGroupPicker';
import { CCUserPicker } from '../../components/ui/CCUserPicker';
import {
  DTOPaginatedUserSearchResultItem,
  DTOPaginatedGroupSearchResultItem,
  ErrorOrPayload,
  GET_CUSTOMFIELD_PERMISSIONS_PAGINATED_GROUPS,
  GET_CUSTOMFIELD_PERMISSIONS_PAGINATED_USERS,
  GET_CUSTOMFIELD_PERMISSIONS_ALL_GROUPIDS_USERIDS,
  PATCH_GROUP_PERMISSIONS,
  PATCH_USER_PERMISSIONS,
  PermissionEditorDTOGetFieldPermissions,
  StoragePermissionDTOPatchGroupPermissions,
  StoragePermissionDTOPatchUserPermissions,
  StoragePermissionDTOUpdateType,
  DTOFieldPermissionGroupIdsAndAccountIdsOnly,
} from '../../generated-common';
import { LoadingState } from '../../model/model';
import { GlobalContext } from '../../shared-components/common/GlobalContext';
import { PermissionEditorSetAndGetPermissionsGroupList } from './PermissionEditorSetAndGetPermissionsGroupList';
import { PermissionEditorSetAndGetPermissionsUserList } from './PermissionEditorSetAndGetPermissionsUserList';

export interface PermissionAction {
  type:
    | 'NOOP'
    | 'INITIAL_LOADING'
    | 'FETCH_PERMISSION_IDS_ONLY'
    | 'REVOKE_USER_PERMISSION'
    | 'REVOKE_GROUP_PERMISSION'
    | 'FETCH_PERMISSION_GROUPS'
    | 'FETCH_PERMISSION_USERS'
    | 'FETCH_PERMISSION_GROUPS_AND_USERS_NEXT'
    | 'FETCH_PERMISSION_IDS_ONLY_AND_USERS_NEXT'
    | 'FETCH_PERMISSION_IDS_ONLY_AND_GROUPS_NEXT'
    | 'GRANT_USER_PERMISSION'
    | 'GRANT_GROUP_PERMISSION';
  payload: any;
}
interface PermissionPaginationState {
  totalPages: number;
  pagesForPaginationComponent: number[];
}
const MAX_RESULTS_USER_OR_GROUPS = 15;
export const calculatePaginationStatePermissions = (_startAt: number, maxResults: number, totalResults: number) => {
  const totalPages = Math.ceil(totalResults / maxResults);
  return {
    totalPages: totalPages,
    pagesForPaginationComponent: Array.from({ length: totalPages }, (v, k) => k + 1),
  } as PermissionPaginationState;
};

export const PermissionEditorSetAndGetPermissions = ({ fieldId, contextId }: { fieldId: string; contextId: number | null }) => {
  const [isFetching, setFetching] = useState<LoadingState>(LoadingState.NOT_LOADED);
  const [isMutating, setMutating] = useState<LoadingState>(LoadingState.NOT_LOADED);
  const [fieldPermissionAllIdsOnly, setFieldPermissionAllIdsOnly] = useState<DTOFieldPermissionGroupIdsAndAccountIdsOnly>({ userIds: [], groupIds: [] });
  const [fieldPermissionUsers, setFieldPermissionUsers] = useState<DTOPaginatedUserSearchResultItem>({ maxResults: 0, startAt: 0, total: 0, isLast: true, values: [] });
  const [fieldPermissionGroups, setFieldPermissionGroups] = useState<DTOPaginatedGroupSearchResultItem>({ maxResults: 0, startAt: 0, total: 0, isLast: true, values: [] });
  const [currentUserStartAt, setCurrentUserStartAt] = useState<number>(0);
  const [currentUserPage, setUserCurrentPage] = useState<number>(1);
  const [currentUserPaginationState, setUserCurrentPaginationState] = useState<PermissionPaginationState>({
    totalPages: 1,
    pagesForPaginationComponent: [1],
  });
  const [currentGroupStartAt, setCurrentGroupStartAt] = useState<number>(0);
  const [currentGroupPage, setGroupCurrentPage] = useState<number>(1);
  const [currentGroupPaginationState, setGroupCurrentPaginationState] = useState<PermissionPaginationState>({
    totalPages: 1,
    pagesForPaginationComponent: [1],
  });

  const [doAction, setDoAction] = useState<PermissionAction>({ type: 'INITIAL_LOADING', payload: null });
  // const [showJson, setShowJson] = useState<boolean>(false);
  const { invoke, handleRestError, t } = useContext(GlobalContext);

  const doNoopOrReFetchGroups = (error: null | ErrorOrPayload<any>) => {
    if (error && error.hasError) {
      setDoAction({ type: 'NOOP', payload: null });
    } else {
      setDoAction({ type: 'FETCH_PERMISSION_IDS_ONLY_AND_GROUPS_NEXT', payload: null });
    }
  };
  const doNoopOrReFetchUsers = (error: null | ErrorOrPayload<any>) => {
    if (error && error.hasError) {
      setDoAction({ type: 'NOOP', payload: null });
    } else {
      setDoAction({ type: 'FETCH_PERMISSION_IDS_ONLY_AND_USERS_NEXT', payload: null });
    }
  };
  const fetchAllGroupAndUserIds = (nextOps: PermissionAction) => {
    // NEEDED FOR THE ALREADY ASSIGNED CHECKS! NO PGANIATION!
    setFetching(LoadingState.LOADING);
    invoke(GET_CUSTOMFIELD_PERMISSIONS_ALL_GROUPIDS_USERIDS, {
      fieldId: fieldId,
      contextId: contextId,
      maxResults: 20 /* UNUSED IN BACKEND, RETURNS 2000 results = ALL */,
      startAt: 0,
    } as PermissionEditorDTOGetFieldPermissions).then((errorOrPayload: null | ErrorOrPayload<DTOFieldPermissionGroupIdsAndAccountIdsOnly>) => {
      handleRestError(errorOrPayload, GET_CUSTOMFIELD_PERMISSIONS_ALL_GROUPIDS_USERIDS);
      if (errorOrPayload && !errorOrPayload?.hasError) {
        setFieldPermissionAllIdsOnly(errorOrPayload.payload);
      }
      setFetching(LoadingState.LOADED);
      setDoAction(nextOps);
    });
  };
  const fetchPaginatedGroups = (nextOps: PermissionAction) => {
    setFetching(LoadingState.LOADING);
    invoke(GET_CUSTOMFIELD_PERMISSIONS_PAGINATED_GROUPS, {
      fieldId: fieldId,
      contextId: contextId,
      maxResults: MAX_RESULTS_USER_OR_GROUPS,
      startAt: currentGroupStartAt,
    } as PermissionEditorDTOGetFieldPermissions).then((errorOrPayload: null | ErrorOrPayload<DTOPaginatedGroupSearchResultItem>) => {
      handleRestError(errorOrPayload, GET_CUSTOMFIELD_PERMISSIONS_PAGINATED_GROUPS);
      if (errorOrPayload && !errorOrPayload?.hasError) {
        setFieldPermissionGroups(errorOrPayload.payload);
        setGroupCurrentPaginationState(calculatePaginationStatePermissions(errorOrPayload.payload.startAt, errorOrPayload.payload.maxResults, errorOrPayload.payload.total));
      }
      setFetching(LoadingState.LOADED);
      setDoAction(nextOps);
    });
  };
  const fetchPaginatedUsers = (nextOps: PermissionAction) => {
    setFetching(LoadingState.LOADING);
    invoke(GET_CUSTOMFIELD_PERMISSIONS_PAGINATED_USERS, {
      fieldId: fieldId,
      contextId: contextId,
      maxResults: MAX_RESULTS_USER_OR_GROUPS,
      startAt: currentUserStartAt,
    } as PermissionEditorDTOGetFieldPermissions).then((errorOrPayload: null | ErrorOrPayload<DTOPaginatedUserSearchResultItem>) => {
      handleRestError(errorOrPayload, GET_CUSTOMFIELD_PERMISSIONS_PAGINATED_USERS);
      if (errorOrPayload && !errorOrPayload?.hasError) {
        setFieldPermissionUsers(errorOrPayload.payload);
        setUserCurrentPaginationState(calculatePaginationStatePermissions(errorOrPayload.payload.startAt, errorOrPayload.payload.maxResults, errorOrPayload.payload.total));
      }
      setFetching(LoadingState.LOADED);
      setDoAction(nextOps);
    });
  };

  useEffect(() => {
    //
    // REACT TO GROUP PAGINATION AND CALCULATE NEW STARTAT
    //
    if (doAction.type !== 'INITIAL_LOADING') {
      // PREVENT DUPLICATER LOADING ON PAGE LOAD!
      const startAt = (currentGroupPage - 1) * MAX_RESULTS_USER_OR_GROUPS;
      setCurrentGroupStartAt(startAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupPage]);

  useEffect(() => {
    //
    // REACT TO NEW GROUP STARTAT AND REFETCH
    //
    if (doAction.type !== 'INITIAL_LOADING') {
      // PREVENT DUPLICATER LOADING ON PAGE LOAD!
      fetchPaginatedGroups({ type: 'NOOP', payload: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupStartAt]);

  useEffect(() => {
    //
    // REACT TO USER PAGINATION AND CALCULATE NEW STARTAT
    //
    if (doAction.type !== 'INITIAL_LOADING') {
      // PREVENT DUPLICATER LOADING ON PAGE LOAD!
      const startAt = (currentUserPage - 1) * MAX_RESULTS_USER_OR_GROUPS;
      setCurrentUserStartAt(startAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserPage]);

  useEffect(() => {
    //
    // REACT TO NEW USER STARTAT AND REFETCH
    //
    if (doAction.type !== 'INITIAL_LOADING') {
      // PREVENT DUPLICATER LOADING ON PAGE LOAD!
      fetchPaginatedUsers({ type: 'NOOP', payload: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserStartAt]);

  useEffect(() => {
    //
    // REACT TO ACTIONS
    //
    if (doAction.type === 'INITIAL_LOADING' && fieldId) {
      fetchAllGroupAndUserIds({ type: 'FETCH_PERMISSION_GROUPS_AND_USERS_NEXT', payload: null });
    }
    if (doAction.type === 'FETCH_PERMISSION_GROUPS_AND_USERS_NEXT' && fieldId) {
      fetchPaginatedGroups({ type: 'FETCH_PERMISSION_USERS', payload: null });
    }
    if (doAction.type === 'FETCH_PERMISSION_IDS_ONLY_AND_USERS_NEXT' && fieldId) {
      fetchAllGroupAndUserIds({ type: 'FETCH_PERMISSION_USERS', payload: null });
    }
    if (doAction.type === 'FETCH_PERMISSION_IDS_ONLY_AND_GROUPS_NEXT' && fieldId) {
      fetchAllGroupAndUserIds({ type: 'FETCH_PERMISSION_GROUPS', payload: null });
    }
    if (doAction.type === 'FETCH_PERMISSION_IDS_ONLY' && fieldId) {
      fetchAllGroupAndUserIds({ type: 'NOOP', payload: null });
    }
    if (doAction.type === 'FETCH_PERMISSION_GROUPS' && fieldId) {
      fetchPaginatedGroups({ type: 'NOOP', payload: null });
    }
    if (doAction.type === 'FETCH_PERMISSION_USERS' && fieldId) {
      fetchPaginatedUsers({ type: 'NOOP', payload: null });
    }
    if (doAction.type === 'GRANT_GROUP_PERMISSION' && fieldId) {
      setMutating(LoadingState.LOADING);
      invoke(PATCH_GROUP_PERMISSIONS, {
        groupId: doAction.payload.groupId,
        permission: {
          fieldId: fieldId,
          contextId: contextId,
        },
        type: StoragePermissionDTOUpdateType.ADD,
      } as StoragePermissionDTOPatchGroupPermissions).then((error: null | ErrorOrPayload<any>) => {
        handleRestError(error, PATCH_GROUP_PERMISSIONS);
        setMutating(LoadingState.LOADED);
        doNoopOrReFetchGroups(error);
      });
    }
    if (doAction.type === 'REVOKE_GROUP_PERMISSION' && fieldId) {
      setMutating(LoadingState.LOADING);
      invoke(PATCH_GROUP_PERMISSIONS, {
        groupId: doAction.payload.groupId,
        permission: {
          fieldId: fieldId,
          contextId: contextId,
        },
        type: StoragePermissionDTOUpdateType.DELETE,
      } as StoragePermissionDTOPatchGroupPermissions).then((error: null | ErrorOrPayload<any>) => {
        setMutating(LoadingState.LOADED);
        handleRestError(error, PATCH_GROUP_PERMISSIONS);
        doNoopOrReFetchGroups(error);
      });
    }
    if (doAction.type === 'GRANT_USER_PERMISSION' && fieldId) {
      setMutating(LoadingState.LOADING);
      invoke(PATCH_USER_PERMISSIONS, {
        userAccountId: doAction.payload.accountId,
        permission: {
          fieldId: fieldId,
          contextId: contextId,
        },
        type: StoragePermissionDTOUpdateType.ADD,
      } as StoragePermissionDTOPatchUserPermissions).then((error: null | ErrorOrPayload<any>) => {
        setMutating(LoadingState.LOADED);
        handleRestError(error, PATCH_USER_PERMISSIONS);
        doNoopOrReFetchUsers(error);
      });
    }
    if (doAction.type === 'REVOKE_USER_PERMISSION' && fieldId) {
      setMutating(LoadingState.LOADING);
      invoke(PATCH_USER_PERMISSIONS, {
        userAccountId: doAction.payload.accountId,
        permission: {
          fieldId: fieldId,
          contextId: contextId,
        },
        type: StoragePermissionDTOUpdateType.DELETE,
      } as StoragePermissionDTOPatchUserPermissions).then((error: null | ErrorOrPayload<any>) => {
        setMutating(LoadingState.LOADED);
        handleRestError(error, PATCH_USER_PERMISSIONS);
        doNoopOrReFetchUsers(error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doAction]);

  return (
    <>
      <Tabs id="default">
        <TabList>
          <Tab>{t('permissions.groupPermissions')}</Tab>
          <Tab>{t('permissions.userPermissions')}</Tab>
        </TabList>
        <TabPanel>
          <div style={{ width: '100%', marginTop: '20px' }}>
            <CCGroupPicker
              alreadyAssignedGroupIds={fieldPermissionAllIdsOnly && fieldPermissionAllIdsOnly.groupIds ? fieldPermissionAllIdsOnly.groupIds : []}
              onGroupPicked={(groupId, groupName) => {
                setDoAction({ type: 'GRANT_GROUP_PERMISSION', payload: { groupId: groupId /*, groupName: groupName */ } });
              }}
              isMutating={isMutating === LoadingState.LOADING}
            />
            <PermissionEditorSetAndGetPermissionsGroupList
              isFetching={isFetching === LoadingState.LOADING}
              isMutating={isMutating === LoadingState.LOADING}
              groups={fieldPermissionGroups && fieldPermissionGroups.values ? fieldPermissionGroups.values : []}
              onDeleteClick={(groupIdToDelete: string) => {
                setDoAction({
                  type: 'REVOKE_GROUP_PERMISSION',
                  payload: {
                    groupId: groupIdToDelete,
                  },
                });
              }}
            />
            {fieldPermissionGroups.values && currentGroupPaginationState && currentGroupPaginationState.totalPages > 1 ? (
              <div style={{ width: '100%', marginTop: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  pages={currentGroupPaginationState.pagesForPaginationComponent}
                  isDisabled={isFetching === LoadingState.LOADING || isMutating === LoadingState.LOADING}
                  selectedIndex={currentGroupPage - 1}
                  onChange={(e: any, i: number) => {
                    setGroupCurrentPage(i);
                  }}
                />
              </div>
            ) : null}
          </div>
        </TabPanel>
        <TabPanel>
          <div style={{ width: '100%', marginTop: '20px' }}>
            <CCUserPicker
              alreadyAssignedUserAccountIds={fieldPermissionAllIdsOnly && fieldPermissionAllIdsOnly.userIds ? fieldPermissionAllIdsOnly.userIds : []}
              onUserPicked={(userAccountId: string) => {
                setDoAction({ type: 'GRANT_USER_PERMISSION', payload: { accountId: userAccountId } });
              }}
              isMutating={isMutating === LoadingState.LOADING}
            />
            <PermissionEditorSetAndGetPermissionsUserList
              isFetching={isFetching === LoadingState.LOADING}
              isMutating={isMutating === LoadingState.LOADING}
              users={fieldPermissionUsers && fieldPermissionUsers.values ? fieldPermissionUsers.values : []}
              onDeleteClick={(userAccountIdToDelete: string) => {
                setDoAction({
                  type: 'REVOKE_USER_PERMISSION',
                  payload: {
                    accountId: userAccountIdToDelete,
                  },
                });
              }}
            />
            {fieldPermissionUsers.values && currentUserPaginationState && currentUserPaginationState.totalPages > 1 ? (
              <div style={{ width: '100%', marginTop: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  pages={currentUserPaginationState.pagesForPaginationComponent}
                  selectedIndex={currentUserPage - 1}
                  isDisabled={isFetching === LoadingState.LOADING || isMutating === LoadingState.LOADING}
                  onChange={(e: any, i: number) => {
                    setUserCurrentPage(i);
                  }}
                />
              </div>
            ) : null}
          </div>
        </TabPanel>
      </Tabs>
    </>
  );
};
